import { AlertTriangle, Check, Info } from 'lucide-react'
import { FitRetryStatus } from '@/components/search/analysis-retry-button'
import { Button } from '@/components/ui/button'
import {
  candidateMatchBandFitChromeClass,
  type CandidateMatchBand,
} from '@/lib/candidate-match-bands'
import { cn } from '@/lib/utils'

export type FitInsightPreviewProps = {
  highlights?: string[]
  redFlags?: string[]
  caption?: string | null
  recommendation?: string | null
  pending?: boolean
  notAnalysed?: boolean
  stuck?: boolean
  onRetry?: () => void
  retryPending?: boolean
  onShowDetails?: () => void
  variant?: 'detailed' | 'compact'
  /** When strong/good, tint the preview box so good matches scan faster. */
  band?: CandidateMatchBand
  className?: string
}

/** Compact fit preview for job cards and application surfaces. */
export function FitInsightPreview({
  highlights = [],
  redFlags = [],
  caption,
  recommendation,
  pending = false,
  notAnalysed = false,
  stuck = false,
  onRetry,
  retryPending = false,
  onShowDetails,
  variant = 'detailed',
  band = null,
  className,
}: FitInsightPreviewProps) {
  if (stuck) {
    return (
      <FitRetryStatus
        tone="candidate"
        state="stuck"
        onRetry={onRetry}
        retryPending={retryPending}
        className={className}
      />
    )
  }

  if (pending) {
    return <FitRetryStatus tone="candidate" state="pending" className={className} />
  }

  if (notAnalysed) {
    return (
      <FitRetryStatus
        tone="candidate"
        state="missing"
        onRetry={onRetry}
        retryPending={retryPending}
        className={className}
      />
    )
  }

  const trimmedRecommendation = recommendation?.trim() ?? ''
  const compactLine = highlights[0] ?? trimmedRecommendation

  if (variant === 'compact') {
    const hasCaption = Boolean(caption?.trim())
    if (!compactLine && !hasCaption) {
      return null
    }

    return (
      <div
        className={cn(
          'space-y-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm',
          candidateMatchBandFitChromeClass(band),
          className,
        )}
      >
        {compactLine ? (
          <p className="line-clamp-1 text-sm text-muted-foreground">{compactLine}</p>
        ) : null}
        {hasCaption ? (
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{caption}</span>
          </p>
        ) : null}
      </div>
    )
  }

  const hasHighlights = highlights.length > 0
  const hasRedFlags = redFlags.length > 0
  const hasCaption = Boolean(caption?.trim())
  const hasBody = hasHighlights || hasRedFlags || trimmedRecommendation

  if (!hasBody && !hasCaption && !onShowDetails) {
    return null
  }

  return (
    <div
      className={cn(
        'space-y-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm',
        candidateMatchBandFitChromeClass(band),
        className,
      )}
    >
      {hasHighlights ? (
        <ul className="space-y-1">
          {highlights.slice(0, 2).map((item) => (
            <li key={item} className="flex items-start gap-2 text-muted-foreground">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {hasRedFlags ? (
        <ul className="space-y-1">
          {redFlags.slice(0, 2).map((item) => (
            <li key={item} className="flex items-start gap-2 text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {!hasHighlights && !hasRedFlags && trimmedRecommendation ? (
        <p className="text-muted-foreground">{trimmedRecommendation}</p>
      ) : null}
      {hasCaption ? (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{caption}</span>
        </p>
      ) : null}
      {onShowDetails ? (
        <Button
          type="button"
          variant="link"
          size="inline"
          className="pointer-events-auto h-auto p-0"
          onClick={(event) => {
            event.stopPropagation()
            onShowDetails()
          }}
        >
          Show details
        </Button>
      ) : null}
    </div>
  )
}
